import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { VNDropdown, VNDropdownOUOptions } from '@/components/VNDropdown';
import type { DropdownOption } from '@/components/VNDropdown';
import { useWebSocketContext } from '@/contexts/WebSocketProvider';
import type { AccessibleOU } from '@/models/FuelUserAccess';
import type { SiteSettings } from '@/models/site';
import { LocalStorageService } from '@/services/local-storage';
import { FuelEntityType, FuelPortalService } from '@/services';
import { fuelUserAccessSliceSelector } from '@/store/slices/fuelUserAccess';
import { treeViewUtils } from '@/utils';
import { cacheUtils } from '@/utils/cacheUtils';

const OuSwitcher = () => {
  const { accessibleOUs } = useSelector(fuelUserAccessSliceSelector);
  const { closeWebSocket } = useWebSocketContext();
  const { fuelUserAccessData } = useSelector(fuelUserAccessSliceSelector);
  const { defaultLoginOu: defaultUserOu } = fuelUserAccessData || {};

  const navigate = useNavigate();

  const ouOptions = useMemo(
    () => treeViewUtils.flattenOUTree(accessibleOUs),
    [accessibleOUs]
  );

  const selectedOu = useMemo(() => {
    if (!accessibleOUs) return null;

    let defaultOU: AccessibleOU | undefined;

    // First go to the Local Storage
    const defaultLoginOu = LocalStorageService.getItem('defaultLoginOu');
    if (defaultLoginOu) {
      defaultOU = accessibleOUs.find((o) => o.ouId === defaultLoginOu);
    }

    // Next try the fuelUserAccessData.defaultLoginOu
    if (!defaultOU && defaultUserOu) {
      defaultOU = accessibleOUs.find((o) => o.ouId === defaultLoginOu);
      if (defaultOU && defaultOU.ouId) {
        // console.log('setting defaultLoginOu to', defaultOU.ouId);
        LocalStorageService.setItem('defaultLoginOu', defaultOU.ouId);
      }
    }

    // Next just get the first ou if nothing else
    if (!defaultOU) {
      const finalData = treeViewUtils.createDataTree(accessibleOUs);
      [defaultOU] = finalData;
      if (defaultOU && defaultOU.ouId) {
        // console.log('setting defaultLoginOu to', defaultOU.ouId);
        LocalStorageService.setItem('defaultLoginOu', defaultOU.ouId);
      }
    }

    // console.log('getSelectedOu found', defaultOU.ouId);
    return defaultOU;
  }, [accessibleOUs, defaultUserOu]);

  const handleOpenSiteUrl = async (option: DropdownOption) => {
    const ou = accessibleOUs.find((o) => o.ouId === option.id);
    if (!ou?.siteId) return;
    const site = await FuelPortalService.getEntityById<SiteSettings>(FuelEntityType.Site, ou.siteId);
    const domain = site?.primaryDomain ?? site?.domains?.[0];
    if (domain) {
      const url = domain.startsWith('http') ? domain : `https://${domain}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleSwitchOu = async (accessibleOU: AccessibleOU) => {
    // console.log('handleSwitchOu', { ouId });
    if (accessibleOU) {
      const location: string = window.location.pathname;
      // console.log('handleSwitchOu', { location });
      LocalStorageService.setItem('defaultLoginOu', accessibleOU.ouId);

      // close websocket connection
      closeWebSocket();

      // Special conditions for navigating away from the editor screens before refreshing
      if (location) {
        if (location.startsWith('/dashboards/app-analytics')) {
          // /channelmanager/editor/83ace869-43b6-4044-87e3-17308855cd28
          LocalStorageService.setItem('RELOAD_ON_NAVIGATION', true);
          navigate('/dashboards/app-analytics');
          return;
        }
        if (location.startsWith('/dashboards/streaming-analytics')) {
          LocalStorageService.setItem('RELOAD_ON_NAVIGATION', true);
          navigate('/dashboards/streaming-analytics/overview');
          return;
        }
        if (location.startsWith('/dashboards/usage')) {
          // /channelmanager/editor/83ace869-43b6-4044-87e3-17308855cd28
          LocalStorageService.setItem('RELOAD_ON_NAVIGATION', true);
          navigate('/dashboards/usage');
          return;
        }
        if (location.startsWith('/channelmanager/editor')) {
          // /channelmanager/editor/83ace869-43b6-4044-87e3-17308855cd28
          LocalStorageService.setItem('RELOAD_ON_NAVIGATION', true);
          navigate('/channelmanager/list');
          return;
        }
        if (location.startsWith('/live/programs/editor')) {
          // /channelmanager/live/programs/editor/eaadda7c-c352-4fe7-927c-cd4bc53abcbc
          LocalStorageService.setItem('RELOAD_ON_NAVIGATION', true);
          navigate('/live/programs/list');
          return;
        }
        if (location.startsWith('/transitiontemplates/editor')) {
          // eg. /channelmanager/transitiontemplates/editor/e7df3d3d-746c-48f0-86d0-fa509fcd8acc
          LocalStorageService.setItem('RELOAD_ON_NAVIGATION', true);
          navigate('/transitiontemplates/list');
          return;
        }
        if (location.startsWith('/series/editor')) {
          // eg. /channelmanager/transitiontemplates/editor/e7df3d3d-746c-48f0-86d0-fa509fcd8acc
          LocalStorageService.setItem('RELOAD_ON_NAVIGATION', true);
          navigate('/series/list');
          return;
        }
        if (location.startsWith('/live/guide/editor')) {
          LocalStorageService.setItem('RELOAD_ON_NAVIGATION', true);
          navigate('/live/guide/list');
          return;
        }
        if (location.startsWith('/live/inputs/editor')) {
          LocalStorageService.setItem('RELOAD_ON_NAVIGATION', true);
          navigate('/live/inputs/list');
          return;
        }
        if (location.startsWith('/movies/editor')) {
          LocalStorageService.setItem('RELOAD_ON_NAVIGATION', true);
          navigate('/movies/list');
          return;
        }
        if (location.startsWith('/monetization/products/editor')) {
          LocalStorageService.setItem('RELOAD_ON_NAVIGATION', true);
          navigate('/monetization/products/list');
          return;
        }
        if (location.startsWith('/monetization/coupons/editor')) {
          LocalStorageService.setItem('RELOAD_ON_NAVIGATION', true);
          navigate('/monetization/coupons/list');
          return;
        }
        if (location.startsWith('/account/app-users/editor')) {
          LocalStorageService.setItem('RELOAD_ON_NAVIGATION', true);
          navigate('/account/app-users/list');
          return;
        }
        if (
          location.startsWith('/live/input-sources/') &&
          !location.startsWith('/live/input-sources/list')
        ) {
          LocalStorageService.setItem('RELOAD_ON_NAVIGATION', true);
          navigate('/live/input-sources/list');
          return;
        }
        if (
          location.startsWith('/live/broadcasts/') &&
          !location.startsWith('/live/broadcasts/list')
        ) {
          LocalStorageService.setItem('RELOAD_ON_NAVIGATION', true);
          navigate('/live/broadcasts/list');
          return;
        }
      }

      // For all other pages we can simply refresh the page
      LocalStorageService.removeItem('RELOAD_ON_NAVIGATION');
      cacheUtils.doHardRefresh();
    }
  };

  if (!accessibleOUs.length) return null;

  return (
    <VNDropdown
      options={ouOptions}
      value={selectedOu?.ouId ?? null}
      onChange={(id) => {
        const found = accessibleOUs.find((o) => o.ouId === id);
        if (found) handleSwitchOu(found);
      }}
      placeholder="Search Portals"
      className="vn-dropdown--nav"
      popoverWidth={400}
      searchable="inset"
      listComponent={(props) => (
        <VNDropdownOUOptions
          {...props}
          link={handleOpenSiteUrl}
        />
      )}
      disableBorder
    />
  );
};

export default OuSwitcher;
